import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Wallet, Smartphone, Landmark, Check, X as XIcon } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const balanceStatusColor: Record<string, string> = { cleared: 'success', partial: 'warning', unpaid: 'danger' };
const paymentStatusColor: Record<string, string> = { completed: 'success', pending: 'warning', rejected: 'danger' };

function PaymentDetailsCard({ institutionId }: { institutionId: string }) {
  const [inst, setInst] = useState<any>(null);

  useEffect(() => {
    if (institutionId) api.get(`/institutions/${institutionId}`).then(r => setInst(r.data.data));
  }, [institutionId]);

  if (!inst) return null;
  const hasMpesa = inst.mpesa_paybill || inst.mpesa_till_number;
  const hasBank = inst.bank_name || inst.bank_account_number;
  if (!hasMpesa && !hasBank && !inst.payment_instructions) return null;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Where to Pay</h3>
      <div className="grid-2" style={{ gap: 16 }}>
        {hasMpesa && (
          <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 10, display: 'flex', gap: 12 }}>
            <Smartphone size={20} style={{ color: 'var(--success)', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>M-Pesa</div>
              {inst.mpesa_paybill && <div style={{ fontSize: 13 }}>Paybill: <strong className="mono">{inst.mpesa_paybill}</strong></div>}
              {inst.mpesa_till_number && <div style={{ fontSize: 13 }}>Till: <strong className="mono">{inst.mpesa_till_number}</strong></div>}
              {inst.mpesa_account_name && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inst.mpesa_account_name}</div>}
            </div>
          </div>
        )}
        {hasBank && (
          <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 10, display: 'flex', gap: 12 }}>
            <Landmark size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{inst.bank_name || 'Bank'}</div>
              {inst.bank_account_number && <div style={{ fontSize: 13 }}>A/C: <strong className="mono">{inst.bank_account_number}</strong></div>}
              {inst.bank_account_name && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inst.bank_account_name}</div>}
              {inst.bank_branch && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inst.bank_branch} Branch</div>}
            </div>
          </div>
        )}
      </div>
      {inst.payment_instructions && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>{inst.payment_instructions}</div>}
    </div>
  );
}

function MpesaStkModal({ balances, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [phase, setPhase] = useState<'form' | 'waiting' | 'done'>('form');
  const [outcome, setOutcome] = useState<any>(null);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const selectStructure = (id: string) => {
    const b = balances.find((x: any) => x.id === id);
    setForm((f: any) => ({ ...f, fee_structure_id: id, academic_year: b?.academic_year, semester: b?.semester, amount: Math.max(b?.balance || 0, 0) || f.amount }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.phone || !form.amount) { toast.error('Phone and amount are required'); return; }
    setPhase('waiting');
    try {
      const res = await api.post('/fees/payments/mpesa/stk-push', form);
      const paymentId = res.data.data.payment?.id;
      if (!res.data.data.mpesa || res.data.data.mpesa.sent === false) {
        toast(res.data.message, { icon: 'ℹ️' });
        setPhase('done'); setOutcome({ status: 'pending' });
        return;
      }
      // Poll for the Daraja callback to land (student enters PIN on their phone).
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const s = await api.get(`/fees/payments/${paymentId}/status`);
        if (s.data.data.status !== 'pending' || attempts >= 20) {
          setOutcome(s.data.data);
          setPhase('done');
          if (s.data.data.status === 'completed') { toast.success('Payment confirmed!'); onSaved(); }
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start STK push');
      setPhase('form');
    }
  };

  return (
    <div className="modal-overlay" onClick={phase === 'form' ? onClose : undefined}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Pay via M-Pesa</h3>{phase === 'form' && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button>}</div>
        {phase === 'form' && (
          <form onSubmit={submit}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {balances.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Fee Period</label>
                  <select className="form-input form-select" value={form.fee_structure_id || ''} onChange={e => selectStructure(e.target.value)}>
                    <option value="">Select fee structure...</option>
                    {balances.map((b: any) => <option key={b.id} value={b.id}>{b.name} — {b.unit_code ? `${b.unit_code} ${b.unit_name}` : `${b.academic_year} Sem ${b.semester}`} (Balance KES {Number(Math.max(b.balance, 0)).toLocaleString()})</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label className="form-label">M-Pesa Phone Number *</label><input className="form-input" required placeholder="07XXXXXXXX" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input type="number" className="form-input" required min={1} value={form.amount || ''} onChange={e => set('amount', e.target.value)} /></div>
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Send STK Push</button></div>
          </form>
        )}
        {phase === 'waiting' && (
          <div className="modal-body" style={{ textAlign: 'center', padding: 32 }}>
            <div className="loader loader-dark" style={{ margin: '0 auto 16px' }} />
            <p style={{ fontWeight: 600 }}>Check your phone</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Enter your M-Pesa PIN on the prompt sent to {form.phone} to complete the payment.</p>
          </div>
        )}
        {phase === 'done' && (
          <>
            <div className="modal-body" style={{ textAlign: 'center', padding: 24 }}>
              {outcome?.status === 'completed' ? (
                <><p style={{ fontWeight: 700, fontSize: 16, color: 'var(--success)' }}>Payment Confirmed</p><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>M-Pesa Receipt: {outcome.mpesa_receipt || '—'}</p></>
              ) : outcome?.status === 'rejected' ? (
                <><p style={{ fontWeight: 700, fontSize: 16, color: 'var(--danger)' }}>Payment Not Completed</p><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{outcome.rejection_reason || 'The request was cancelled or timed out.'}</p></>
              ) : (
                <><p style={{ fontWeight: 700, fontSize: 16 }}>Still Pending</p><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>We'll keep waiting for confirmation — you can check back on this page shortly.</p></>
              )}
            </div>
            <div className="modal-footer"><button className="btn btn-primary" onClick={onClose}>Close</button></div>
          </>
        )}
      </div>
    </div>
  );
}

function MyFeeBalance({ onMakePayment }: { onMakePayment: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showMpesa, setShowMpesa] = useState(false);

  const refresh = useCallback(() => api.get('/fees/my-balances').then(r => setData(r.data.data)), []);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  if (loading) return <div className="card" style={{ textAlign: 'center', padding: 40, marginBottom: 20 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>;
  if (!data || data.balances.length === 0) return null;

  const { balances, summary } = data;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} />My Fee Balance</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowMpesa(true)}><Smartphone size={13} />Pay via M-Pesa</button>
          <button className="btn btn-secondary btn-sm" onClick={onMakePayment}>I've Made a Payment</button>
        </div>
      </div>
      {showMpesa && <MpesaStkModal balances={balances} onClose={() => setShowMpesa(false)} onSaved={() => { setShowMpesa(false); refresh(); }} />}
      <div className="grid-3" style={{ marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total Due</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>KES {Number(summary.total_due).toLocaleString()}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total Paid</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>KES {Number(summary.total_paid).toLocaleString()}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Balance Remaining</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: summary.balance > 0 ? 'var(--danger)' : 'var(--success)' }}>KES {Number(Math.max(summary.balance, 0)).toLocaleString()}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {balances.map((b: any) => (
          <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', borderRadius: 10, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{b.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {b.unit_name ? `${b.unit_code} — ${b.unit_name}` : `${b.academic_year} · Semester ${b.semester}`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Due KES {Number(b.total_due).toLocaleString()} · Paid KES {Number(b.total_paid).toLocaleString()}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: b.balance > 0 ? 'var(--danger)' : 'var(--success)' }}>Balance KES {Number(Math.max(b.balance, 0)).toLocaleString()}</div>
              </div>
              <span className={`badge badge-${balanceStatusColor[b.status]}`} style={{ textTransform: 'capitalize' }}>{b.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Payments() {
  const [payments, setPayments] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const { user } = useAuthStore();
  const canRecord = ['admin', 'finance', 'superadmin'].includes(user?.role || '');
  const isStudent = user?.role === 'student';
  const colCount = 7 + (canRecord ? 1 : 0);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit:20 };
      if (search) params.search = search;
      if (method) params.payment_method = method;
      if (status) params.status = status;
      const res = await api.get('/fees/payments', { params });
      setPayments(res.data.data.payments);
      setSummary(res.data.data.summary||{});
      setTotal(res.data.data.total);
    } finally { setLoading(false); }
  }, [page, search, method, status]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const decide = async (id: string, action: 'approve' | 'reject') => {
    if (action === 'reject' && !window.confirm('Reject this payment claim?')) return;
    try {
      await api.patch(`/fees/payments/${id}/${action}`);
      toast.success(`Payment ${action}d`);
      doFetch();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Fee Payments</h1><p className="page-subtitle">{isStudent ? 'Your payment history' : `${total} confirmed transactions`}</p></div>
        {canRecord && <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Record Payment</button>}
      </div>
      {isStudent && <PaymentDetailsCard institutionId={user?.institution_id} />}
      {isStudent && <MyFeeBalance onMakePayment={() => setShowSubmitModal(true)} />}
      <div className="grid-4" style={{ marginBottom:24 }}>
        {[
          { label: isStudent ? 'Total Paid' : 'Total Collected', value:`KES ${Number(summary.total||0).toLocaleString()}`, color:'#1a56db' },
          { label:'M-Pesa', value:`KES ${Number(summary.mpesa_total||0).toLocaleString()}`, color:'#0e9f6e' },
          { label:'Cash', value:`KES ${Number(summary.cash_total||0).toLocaleString()}`, color:'#d97706' },
          { label:'Bank', value:`KES ${Number(summary.bank_total||0).toLocaleString()}`, color:'#7c3aed' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize:20, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      {summary.pending_count > 0 && (
        <div className="card" style={{ marginBottom: 16, background: '#fef3c7', border: '1px solid #fcd34d' }}>
          <span style={{ color: '#92400e', fontWeight: 600, fontSize: 13 }}>
            {summary.pending_count} payment{summary.pending_count === 1 ? '' : 's'} awaiting approval
            {canRecord && <button className="btn btn-secondary btn-sm" style={{ marginLeft: 12 }} onClick={() => { setStatus('pending'); setPage(1); }}>View Pending</button>}
          </span>
        </div>
      )}
      <div className="card">
        <div className="filters-bar">
          <div className="search-bar"><Search size={15} style={{ color:'var(--text-muted)' }} /><input placeholder={isStudent ? 'Search by receipt number...' : 'Search receipt, student...'} value={search} onChange={e=>{ setSearch(e.target.value); setPage(1); }} /></div>
          <select className="form-input form-select" style={{ width:150 }} value={method} onChange={e=>{ setMethod(e.target.value); setPage(1); }}>
            <option value="">All Methods</option>
            <option value="mpesa">M-Pesa</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
          </select>
          <select className="form-input form-select" style={{ width:170 }} value={status} onChange={e=>{ setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending Approval</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="table-container">
          <table>
            <thead><tr><th>Receipt No.</th><th>Student</th><th>Amount</th><th>Method</th><th>Reference</th><th>Status</th><th>Date</th>{canRecord && <th>Actions</th>}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={colCount} style={{ textAlign:'center', padding:40 }}><div className="loader loader-dark" style={{ margin:'0 auto' }} /></td></tr>
              : payments.length===0 ? <tr><td colSpan={colCount}><div className="empty-state"><p>No payments found</p></div></td></tr>
              : payments.map((p: any) => (
                <tr key={p.id}>
                  <td><span className="mono" style={{ fontSize:12, fontWeight:600 }}>{p.receipt_number}</span></td>
                  <td><div style={{ fontWeight:500 }}>{p.student_name}</div><div style={{ fontSize:12, color:'var(--text-muted)' }}>{p.student_number}</div></td>
                  <td style={{ fontWeight:700, color:'var(--success)', fontSize:15 }}>KES {Number(p.amount).toLocaleString()}</td>
                  <td><span className={`badge badge-${p.payment_method==='mpesa'?'success':p.payment_method==='cash'?'info':'warning'}`}>{p.payment_method?.toUpperCase()}</span></td>
                  <td style={{ fontSize:12 }}>{p.transaction_code||p.bank_reference||'—'}</td>
                  <td><span className={`badge badge-${paymentStatusColor[p.status]||'gray'}`} style={{ textTransform:'capitalize' }}>{p.status}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{new Date(p.payment_date).toLocaleString()}</td>
                  {canRecord && (
                    <td>
                      {p.status === 'pending' ? (
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(p.id, 'approve')}><Check size={13} />Approve</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(p.id, 'reject')}><XIcon size={13} />Reject</button>
                        </div>
                      ) : <span style={{ fontSize:12, color:'var(--text-muted)' }}>{p.received_by_name || '—'}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && canRecord && <RecordPaymentModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); doFetch(); }} />}
      {showSubmitModal && isStudent && <SubmitPaymentModal onClose={() => setShowSubmitModal(false)} onSaved={() => { setShowSubmitModal(false); doFetch(); }} />}
    </div>
  );
}

function SubmitPaymentModal({ onClose, onSaved }: any) {
  const [balances, setBalances] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ payment_method: 'mpesa' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => { api.get('/fees/my-balances').then(r => setBalances(r.data.data.balances || [])); }, []);

  const selectStructure = (id: string) => {
    const b = balances.find(x => x.id === id);
    setForm((f: any) => ({ ...f, fee_structure_id: id, academic_year: b?.academic_year, semester: b?.semester }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.amount || !form.payment_method) { toast.error('Amount and payment method are required'); return; }
    setLoading(true);
    try {
      await api.post('/fees/payments/initiate', form);
      toast.success('Payment submitted — awaiting approval');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to submit payment'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Submit Payment for Approval</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Already sent the money? Tell us the details below and finance will confirm it against your account.</p>
            {balances.length > 0 && (
              <div className="form-group">
                <label className="form-label">Fee Period</label>
                <select className="form-input form-select" value={form.fee_structure_id || ''} onChange={e => selectStructure(e.target.value)}>
                  <option value="">Select fee structure...</option>
                  {balances.map(b => <option key={b.id} value={b.id}>{b.name} — {b.unit_code ? `${b.unit_code} ${b.unit_name}` : `${b.academic_year} Sem ${b.semester}`} (Balance KES {Number(Math.max(b.balance, 0)).toLocaleString()})</option>)}
                </select>
              </div>
            )}
            <div className="form-group"><label className="form-label">Amount (KES) *</label><input type="number" className="form-input" required min={1} value={form.amount || ''} onChange={e => set('amount', e.target.value)} /></div>
            <div className="form-group">
              <label className="form-label">Payment Method *</label>
              <select className="form-input form-select" required value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            {form.payment_method === 'mpesa' && <div className="form-group"><label className="form-label">M-Pesa Code *</label><input className="form-input" required placeholder="e.g. QHF7XXXXXX" value={form.transaction_code || ''} onChange={e => set('transaction_code', e.target.value)} /></div>}
            {form.payment_method === 'bank' && <div className="form-group"><label className="form-label">Bank Reference *</label><input className="form-input" required value={form.bank_reference || ''} onChange={e => set('bank_reference', e.target.value)} /></div>}
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit for Approval'}</button></div>
        </form>
      </div>
    </div>
  );
}

function RecordPaymentModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ payment_method:'cash' });
  const [structures, setStructures] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  useEffect(() => { api.get('/fees/structures').then(r => setStructures(r.data.data)); }, []);
  useEffect(() => {
    if (searchStu.length>=2) api.get('/students', { params:{ search:searchStu, limit:10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true);
    try { await api.post('/fees/payments', form); toast.success('Payment recorded'); onSaved(); } finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight:600 }}>Record Payment</h3><button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="form-group">
              <label className="form-label">Search Student</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e=>setSearchStu(e.target.value)} />
              {stuResults.length>0 && (
                <div style={{ border:'1px solid var(--border)', borderRadius:8, maxHeight:180, overflowY:'auto', marginTop:4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id',s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }}
                      style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Fee Structure</label><select className="form-input form-select" value={form.fee_structure_id||''} onChange={e=>set('fee_structure_id',e.target.value)}><option value="">Select...</option>{structures.map((s:any)=><option key={s.id} value={s.id}>{s.name}{s.unit_code ? ` (${s.unit_code})` : ''} — KES {Number(s.total_amount).toLocaleString()}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Amount (KES) *</label><input type="number" className="form-input" required min={1} value={form.amount||''} onChange={e=>set('amount',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Payment Method *</label><select className="form-input form-select" required value={form.payment_method} onChange={e=>set('payment_method',e.target.value)}><option value="cash">Cash</option><option value="mpesa">M-Pesa</option><option value="bank">Bank</option></select></div>
            {form.payment_method==='mpesa' && <div className="form-group"><label className="form-label">M-Pesa Code</label><input className="form-input" placeholder="e.g. QHF7XXXXXX" value={form.transaction_code||''} onChange={e=>set('transaction_code',e.target.value)} /></div>}
            {form.payment_method==='bank' && <div className="form-group"><label className="form-label">Bank Reference</label><input className="form-input" value={form.bank_reference||''} onChange={e=>set('bank_reference',e.target.value)} /></div>}
            <div className="grid-2" style={{ gap:12 }}>
              <div className="form-group"><label className="form-label">Academic Year</label><input className="form-input" placeholder="2024/2025" value={form.academic_year||''} onChange={e=>set('academic_year',e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Semester</label><select className="form-input form-select" value={form.semester||''} onChange={e=>set('semester',e.target.value)}><option value="">Select</option><option value={1}>Sem 1</option><option value={2}>Sem 2</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" value={form.notes||''} onChange={e=>set('notes',e.target.value)} rows={2} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading?<span className="loader"/>:'Record Payment'}</button></div>
        </form>
      </div>
    </div>
  );
}
